import React from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import {
  calculateBinaryHeaderPercent,
  calculateBinaryPassRate,
  ComparedMetricDistributionChart,
  deriveYesNoString,
  hasBinaryUnknowns,
  resolveBinaryTargetCondition,
  resolveSuccessValue,
  StringValueYesNoChip,
} from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import { compareBinaryMetricValues } from "@/metrics/runtime/record-data"

import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { TargetCondition } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { BinaryCompareByPctChart } from "../../components/charts/BinaryCompareByPctChart"
import { SingleBinary } from "../../components/single/SingleBinary"

/**
 * Type base class for binary (YES/NO/UNKNOWN) string output metrics.
 *
 * Contains all output-format-specific rendering as conditional branches in MetricRecord.
 * No metric-specific logic here — extend this class for for it.
 */
export class BinaryMetricRecord extends MetricRecord {
  overviewSize = "small" as const
  aggregate_units = "%"
  invertYesNoColors = false

  hasValidOutputValue(mo: RunMetricOutput): boolean {
    return deriveYesNoString(mo) !== ANNOTATION_VALUE.UNKNOWN
  }

  getEffectiveTargetCondition(): TargetCondition {
    return resolveBinaryTargetCondition(this, this.invertYesNoColors)
  }

  getBinaryAggregateOutputs(mo: RunMetricOutput[]): RunMetricOutput[] {
    return mo
  }

  // ─── Comparison ──────────────────────────────────────────────────────────

  compareValues(
    value1: { float_value?: number | null; string_value?: string | null },
    value2: { float_value?: number | null; string_value?: string | null },
  ): boolean {
    return compareBinaryMetricValues(value1, value2)
  }

  // ─── Badge ────────────────────────────────────────────────────────────────

  // Badge: Yes / No / Unknown (N/A)
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return <StringValueYesNoChip mo={mo} metric={this} />
  }

  // ─── Single output ────────────────────────────────────────────────────────

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleBinary mo={mo} metric={this} />
  }

  // ─── Overview / run review ────────────────────────────────────────────────

  getOverviewNode(
    mo: RunMetricOutput[],
    controlledProps?: {
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode {
    for (const m of mo) {
      m.string_value = deriveYesNoString(m)
    }
    return (
      <AggregateOverviewString
        mos={mo}
        isBinary
        targetCondition={this.getEffectiveTargetCondition()}
        includeUnknowns={controlledProps?.includeUnknowns}
        onIncludeUnknownsChange={controlledProps?.onIncludeUnknownsChange}
        hideCheckbox={controlledProps?.hideCheckbox}
      />
    )
  }

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const data = allGroupKeys.map((key) => {
      const groupMo = groups.get(key) ?? []
      const pct =
        (calculateBinaryPassRate(
          this.getBinaryAggregateOutputs(groupMo),
          true,
          this.getEffectiveTargetCondition(),
        ) ?? 0) * 100
      return {
        label: getCompareByDisplayLabel(key, compareBy),
        value: pct,
        color: compareBy.colors[key],
      }
    })
    return (
      <BinaryCompareByPctChart
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        data={data}
        valueLabel="Success %"
      />
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    if (hasBinaryUnknowns(mo)) {
      return null
    }
    return calculateBinaryHeaderPercent(
      mo,
      false,
      this.getEffectiveTargetCondition(),
    )
  }

  // Failure rate for "YES = bad" metrics: % of outputs that are NOT the success
  // value (0% = healthy). deriveYesNoString lets float-output rows (null
  // string_value) count, consistent with getOverviewNode.
  protected getFailureRateHeaderValue(mo: RunMetricOutput[]): string | null {
    const passRate = calculateBinaryPassRate(
      mo,
      false,
      this.getEffectiveTargetCondition(),
    )
    return passRate === null ? null : `${((1 - passRate) * 100).toFixed(0)}%`
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={metricOutputs}
          isBinary
          targetCondition={this.getEffectiveTargetCondition()}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  renderComparedInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    // Ensure string_value is populated from deriveYesNoString for legacy metric outputs
    // that may only have float_value, and force output_type to "string" for the chart.
    const normalizedOutputs = metricOutputs.map((m) => ({
      ...m,
      string_value: deriveYesNoString(m),
      output_type: "string" as const,
    }))
    const successValue = resolveSuccessValue(this, this.invertYesNoColors)
    return (
      <ComparedMetricDistributionChart
        metricOutputs={normalizedOutputs}
        runRows={runRows}
        metricRecord={this}
        compareBy={compareBy}
        successValue={successValue}
      />
    )
  }

  // ─── Alert / filter forms ─────────────────────────────────────────────────

  hasUnknowns(mo: RunMetricOutput[]): boolean {
    return mo.some((m) => m.string_value?.trim() === ANNOTATION_VALUE.UNKNOWN)
  }

  /**
   * Provide numeric values for grouped aggregation modes (avg / median / p95 / min / max).
   * For binary metrics we treat the "success" value as 100 and all other
   * non-unknown values as 0, so:
   * - Average gives the success rate in percent.
   * - P95 / median / min / max give 0% or 100% depending on distribution.
   */
  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    const condition = resolveBinaryTargetCondition(this, this.invertYesNoColors)

    return metricOutputs
      .map((m) => {
        const value = deriveYesNoString(m)
        if (value === ANNOTATION_VALUE.UNKNOWN) {
          return null
        }
        const inTarget = condition.target_values.includes(value)
        const passes =
          condition.comparison_operator === "in" ? inTarget : !inTarget
        return passes ? 100 : 0
      })
      .filter((v): v is 0 | 100 => v !== null)
  }
}
